import { useCallback } from 'react';
import { useSubscription } from '@/hooks/useSubscription';
import { useUpgradeModal, FeatureKey, FEATURE_REQUIRED_PLAN } from '@/contexts/UpgradeModalContext';
import { SubscriptionPlan } from '@/services/billing';

/**
 * Hook to check if a feature is available and show upgrade modal if not.
 * 
 * Usage:
 * const { checkFeature, hasFeature } = useFeatureAccess();
 * 
 * // Check and show modal if needed (returns boolean)
 * if (!checkFeature('automation')) return;
 * 
 * // Just check without showing modal
 * const canUseAutomation = hasFeature('automation');
 */
export const useFeatureAccess = () => {
  const { plan, hasFullAccess, hasBetaAccess, hasAIAccess, hasPrerelease } = useSubscription();
  const { openUpgradeModal } = useUpgradeModal();

  const planOrder: SubscriptionPlan[] = ['free', 'orbit_starter', 'orbit', 'orbit_business'];

  /**
   * Check if user has access to a feature.
   * Considers both subscription plan AND entitlement overrides.
   * Does NOT show upgrade modal.
   */
  const hasFeature = useCallback((feature: FeatureKey): boolean => {
    // Full access entitlement bypasses all plan checks
    if (hasFullAccess) {
      return true;
    }
    
    // Standard plan-based check
    const requiredPlan = FEATURE_REQUIRED_PLAN[feature];
    const currentIndex = planOrder.indexOf(plan);
    const requiredIndex = planOrder.indexOf(requiredPlan);
    return currentIndex >= requiredIndex;
  }, [plan, hasFullAccess]);

  /**
   * Check if user has access to a feature.
   * Shows upgrade modal if access is denied.
   * Returns true if user has access, false otherwise.
   */
  const checkFeature = useCallback((feature: FeatureKey): boolean => {
    if (hasFeature(feature)) {
      return true;
    }
    openUpgradeModal(feature);
    return false;
  }, [hasFeature, openUpgradeModal]);

  /**
   * Get the minimum plan required for a feature.
   */
  const getRequiredPlan = useCallback((feature: FeatureKey): SubscriptionPlan => {
    return FEATURE_REQUIRED_PLAN[feature];
  }, []);

  return {
    plan,
    hasFeature,
    checkFeature,
    getRequiredPlan,
    openUpgradeModal,
    // Expose entitlement states for direct checks
    hasFullAccess,
    hasBetaAccess,
    hasAIAccess,
    hasPrerelease,
  };
};

export type { FeatureKey };
